import { Logger } from './core/logger';

// FIXME: This should be centralized. Fix package structure.
const CORRELATION_ID_HEADER = 'X-Correlation-Id';
const APPLICATION_ID_HEADER = 'X-EF-Application';

enum LogTypes {
  Success = 'FetchInterceptor',
  Error = 'FetchInterceptorError',
}
type FetchEnv = Window | typeof global;

interface InterceptHeadersOptions {
  logger: Logger;
  noEmptyCorrelationIds?: boolean;
  persistCorrelationIds?: boolean;
}

interface FetchInterceptorOptions {
  target: FetchEnv;
  logger: Logger;
  headers?: Omit<InterceptHeadersOptions, 'logger'>;
}

/**
 * InterceptedFetchRequestInit forces the headers parameter to be a `Record<string, string>`
 * type to simplify modifying headers.
 */
interface InterceptedFetchRequestInit extends RequestInit {
  headers: Record<string, string>;
}

/**
 * useFetchInterceptor patches a fetch method on a `Window` or global object. All subsequent
 * `fetch` calls will use the patched method.
 *
 * On outgoing requests, the interceptor will:
 * - Add a `X-Correlation-Id` header value if it is not already present
 * - Add a `X-EF-Application` header value from the logger's service name
 *
 * On responses, the interceptor will:
 * - Log the request, including url, method, duration, and status
 * - Log abnormal responses as errors, and re-throw to consuming code
 *
 * @param FetchInterceptorOptions
 * @returns Promise<Response>
 * @example
 * useFetchInterceptor({ target: window, logger, headers: { applicationId: 'my-app' } });
 * await fetch('https://example.com');
 */
const useFetchInterceptor = ({ target, logger, headers: interceptHeadersOptions }: FetchInterceptorOptions) => {
  if (typeof target.fetch !== 'function') {
    throw new TypeError('fetch is not a function on the target object');
  }

  // Store the original fetch method so we can call it later within our patched version
  const fetch = target.fetch;

  // Patch fetch directly on the target object with a new signature that is compatible with the original
  target.fetch = async (input: string | URL, init?: InterceptedFetchRequestInit) => {
    const { method, headers = {}, ...requestInit } = init || {};
    const duration = performance.now();

    // Update the headers with our correlation id and application id
    const interceptedHeaders = interceptHeaders(headers, {
      ...interceptHeadersOptions,
      logger,
    });

    try {
      // Make the request, capture the duration, and log the result
      const response = await fetch(input, { ...requestInit, method, headers: interceptedHeaders });
      const durationMs = performance.now() - duration;

      // FIXME: Determine if there's more here we actually want to log
      logger.request({
        logType: LogTypes.Success,
        message: '',
        url: input.toString(),
        method: method || 'GET',
        status: response.status,
        durationMs,
      });

      return response;
    } catch (err: unknown) {
      // Handle whatever crazy thing happened (fetch only throws on network errors, really), log it, and
      // rethrow it.
      const durationMs = performance.now() - duration;

      logger.error({
        logType: LogTypes.Error,
        message: err instanceof Error ? err : 'Unknown error',
        url: input.toString(),
        method: method || 'GET',
        durationMs,
      });

      throw err;
    }
  };
};

/**
 * interceptHeaders modifies outbound request headers to include a correlation id and application id.
 */
const interceptHeaders = (
  headers: Record<string, string>,
  { logger, noEmptyCorrelationIds = true, persistCorrelationIds = false }: InterceptHeadersOptions
): HeadersInit => {
  let cid = headers[CORRELATION_ID_HEADER];

  // Handle the correlation id by either adding one or using the existing one
  if (!cid && noEmptyCorrelationIds) {
    cid = logger.getCorrelationId() || logger.generateCorrelationId();
    headers[CORRELATION_ID_HEADER] = cid;

    if (persistCorrelationIds) {
      logger.setCorrelationId(cid);
    }
  }

  // Set the application id
  headers[APPLICATION_ID_HEADER] = `${logger.getMeta('service')}`;

  return headers;
};

export { useFetchInterceptor };
